'use client'

import { useSession, signIn } from 'next-auth/react'
import { useEffect, useRef, useCallback } from 'react'
import { useRouter } from 'next/navigation'

/**
 * Hook to monitor session health and handle token refresh errors
 * Automatically triggers re-authentication when refresh fails
 */
export function useSessionRefresh() {
  const { data: session, status, update } = useSession()
  const hasTriggeredSignIn = useRef(false)
  const lastRefreshAttempt = useRef<number>(0)
  const router = useRouter()

  // Force session refresh
  const forceRefresh = useCallback(async () => {
    const now = Date.now()
    // Prevent refresh spam (max once per 30 seconds)
    if (now - lastRefreshAttempt.current < 30000) {
      return
    }
    lastRefreshAttempt.current = now
    
    try {
      console.log('[Session] Manually triggering session refresh...')
      await update()
    } catch (error) {
      console.error('[Session] Manual refresh failed:', error)
    }
  }, [update])

  useEffect(() => {
    // Check if there's a refresh error in the session
    if (session?.error === 'RefreshAccessTokenError' && !hasTriggeredSignIn.current) {
      console.log(
        '[Session] Token refresh failed. Triggering re-authentication...'
      )
      // Mark that we've triggered sign-in to prevent loops
      hasTriggeredSignIn.current = true
      
      // Clear any stale session data
      localStorage.removeItem('nextauth.message')
      
      // Automatically sign in again to get fresh tokens
      // Redirect silently without showing the error to the user
      signIn('keycloak', { 
        callbackUrl: window.location.pathname,
        redirect: true 
      })
    }
    
    // Reset flag if session recovers
    if (session && !session.error) {
      hasTriggeredSignIn.current = false
    }

    // Proactive token refresh when approaching expiry
    if (session?.accessTokenExpires) {
      const expiresAt = session.accessTokenExpires as number
      const timeUntilExpiry = expiresAt - Date.now()
      const refreshThreshold = 3 * 60 * 1000 // 3 minutes before expiry (increased buffer)

      if (timeUntilExpiry > 0 && timeUntilExpiry < refreshThreshold) {
        console.log(`[Session] Token expiring in ${Math.round(timeUntilExpiry / 1000)}s, refreshing...`)
        forceRefresh()
      }
    }
  }, [session, session?.error, session?.accessTokenExpires, forceRefresh])

  return { session, status, forceRefresh }
}

